import React from "react";
import { RouteComponentProps } from "react-router-dom";
import "./CourseAttendance.css";
import "../../App.css";
import { apiRequest } from "../../functions/requestMulti";
import { MainContext } from "../../contexts/MainContext";
import BackElement from "../../components/BackElement";
import Title from "../../components/Title";
import ErrorMessage from "../../components/ErrorMessage";
import SearchInput from "../../components/SearchInput";
import TextCheckbox from "../../components/TextCheckbox";
import Table from "../../components/Table/Table";
import { RowData } from "../../Types";
import {
  AttendanceEntry,
  AttendanceList as AttendanceListData,
  parseSessionKey,
  sessionDateLabel,
} from "../../functions/attendance";

type Props = {};
type ComposedProps = Props &
  RouteComponentProps<{
    clubId: string;
    branchId: string;
    courseId: string;
    sessionKey: string;
  }>;

export default class AttendanceList extends React.Component<
  ComposedProps,
  {
    clubId: string;
    branchId: string;
    courseId: string;
    listLoaded: boolean;
    list: AttendanceListData | null;
    // courseBookingIds with a save in flight; further clicks wait for it
    saving: { [courseBookingId: string]: boolean };
    searchVal: string;
    errorMsg: string | null;
  }
> {
  static contextType = MainContext;
  mounted = true;

  constructor(props: ComposedProps) {
    super(props);
    this.state = {
      clubId: this.props.match.params.clubId,
      branchId: this.props.match.params.branchId,
      courseId: this.props.match.params.courseId,
      listLoaded: false,
      list: null,
      saving: {},
      searchVal: "",
      errorMsg: null,
    };
  }

  componentDidMount = async () => {
    await this.openList();
  };

  componentWillUnmount = () => {
    this.mounted = false;
  };

  // Creates the list on first use and adds participants who booked since, so
  // it is a POST - but an idempotent one, so the default retries are fine.
  openList = async () => {
    const session = parseSessionKey(this.props.match.params.sessionKey);
    if (!session) {
      this.setState({
        listLoaded: true,
        errorMsg: "Dieser Termin ist ungültig.",
      });
      return;
    }
    await apiRequest("api/attendance/list", "POST", {
      clubId: this.state.clubId,
      branchId: this.state.branchId,
      courseId: this.state.courseId,
      date: session.date,
      startTime: session.startTime,
    })
      .then(this.handleList)
      .catch(this.handleListError);
  };

  handleList = ({ data }: any) => {
    if (!this.mounted) return;
    this.setState({ listLoaded: true });
    if (!data.success) {
      this.setState({ errorMsg: data.errorMsgDe });
      return;
    }
    this.setState({ list: data.data, errorMsg: null });
  };

  handleListError = (err: any) => {
    console.error(err);
    if (!this.mounted) return;
    // apiRequest discards the body of a non-2xx response, so there is no
    // errorMsgDe available here.
    this.setState({
      listLoaded: true,
      errorMsg: "Die Anwesenheitsliste konnte nicht geladen werden.",
    });
  };

  setEntryPresent = (courseBookingId: string, present: boolean) => {
    this.setState((state) =>
      state.list
        ? {
            list: {
              ...state.list,
              entries: state.list.entries.map((entry) =>
                entry.courseBookingId === courseBookingId
                  ? { ...entry, present }
                  : entry
              ),
            },
          }
        : null
    );
  };

  setSaving = (courseBookingId: string, saving: boolean) => {
    this.setState((state) => ({
      saving: { ...state.saving, [courseBookingId]: saving },
    }));
  };

  // Ticks optimistically and rolls back if saving fails, so a trainer can
  // work through the list without waiting for every request.
  toggle = async (courseBookingId: string | null) => {
    const list = this.state.list;
    if (!courseBookingId || !list || this.state.saving[courseBookingId]) {
      return;
    }
    const entry = list.entries.find(
      (curr) => curr.courseBookingId === courseBookingId
    );
    if (!entry) return;
    const present = !entry.present;

    this.setEntryPresent(courseBookingId, present);
    this.setSaving(courseBookingId, true);
    const rollBack = (errorMsg: string) => {
      if (!this.mounted) return;
      this.setEntryPresent(courseBookingId, !present);
      this.setState({ errorMsg });
    };

    // tries = 1: a delayed retry could land after a later click and undo it.
    await apiRequest(
      "api/attendance/entry",
      "POST",
      {
        clubId: this.state.clubId,
        branchId: this.state.branchId,
        listId: list.listId,
        courseBookingId,
        present,
      },
      1
    )
      .then(({ data }: any) => {
        if (!data.success) {
          rollBack(data.errorMsgDe);
        } else if (this.mounted) {
          this.setState({ errorMsg: null });
        }
      })
      .catch((err) => {
        console.error(err);
        rollBack(
          `Die Anwesenheit von ${entry.fName} ${entry.lName} konnte nicht gespeichert werden.`
        );
      });
    if (this.mounted) this.setSaving(courseBookingId, false);
  };

  handleSearchChange = (val: string) => {
    this.setState({ searchVal: val });
  };

  entryRows = (): Array<RowData> =>
    (this.state.list?.entries || []).map((entry: AttendanceEntry) => ({
      cells: [
        {
          value: (
            <TextCheckbox
              val={entry.present}
              onChange={(e: React.MouseEvent) => {
                // The row click toggles too; this must not count twice.
                e.stopPropagation();
                this.toggle(entry.courseBookingId);
              }}
            />
          ),
          sort: entry.present ? 1 : 0,
        },
        {
          value: entry.fName,
          sort: entry.fName.toLowerCase(),
          search: `${entry.fName} ${entry.lName}`,
        },
        {
          value: entry.lName,
          sort: entry.lName.toLowerCase(),
          search: `${entry.fName} ${entry.lName}`,
        },
      ],
      // The whole row is the tap target - easier on a phone at the gym door.
      onClick: entry.courseBookingId,
      opacity: this.state.saving[entry.courseBookingId] ? 0.5 : undefined,
    }));

  render() {
    const list = this.state.list;
    const presentCount = (list?.entries || []).filter((e) => e.present).length;

    return (
      <div className="subscreen-main-container">
        <div className="subscreen-box-container">
          <BackElement
            text="zurück zu den Terminen"
            to={`/club/${this.state.clubId}/branch/${this.state.branchId}/attendance/${this.state.courseId}`}
          />
          <Title title="Anwesenheitsliste" />
          {list && (
            <div className="attendance-session-info">
              <h2 className="attendance-course-title">
                {list.courseTitle}
                {list.sessionTitle ? ` - ${list.sessionTitle}` : ""}
              </h2>
              <div>
                {sessionDateLabel(list.date)}, {list.startTime} -{" "}
                {list.endTime} Uhr
              </div>
              <div className="attendance-counter">
                {presentCount} von {list.entries.length} anwesend
              </div>
            </div>
          )}
          <ErrorMessage message={this.state.errorMsg} />
          {list && list.entries.length > 0 && (
            <SearchInput
              value={this.state.searchVal}
              onChange={this.handleSearchChange}
              placeholder="Teilnehmer suchen"
            />
          )}
          <Table
            header={[
              { title: "Anwesend" },
              { title: "Vorname" },
              { title: "Nachname" },
            ]}
            data={this.entryRows()}
            loaded={this.state.listLoaded}
            skeletonRows={8}
            onClick={this.toggle}
            searchValue={this.state.searchVal}
            defaultSortIndex={2}
            defaultSortDirection="asc"
            counter
          />
          {this.state.listLoaded && list && list.entries.length === 0 && (
            <div className="full-center">
              <span className="not-exist-text">
                Für diesen Termin gibt es keine Teilnehmer.
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }
}
